import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import ErrorState from '../ui/ErrorState';
import DropdownMenu from '../ui/DropdownMenu';
import Slideover from '../ui/Slideover';
import { Input } from '../ui/Input';
import { Chip } from '../ui/Chip';
import { SelectMenu, DatePicker, TimePicker, DateJumpPicker, TypeaheadChips, Checkbox } from '../ui';
import api, { calendarAPI, jobAPI, customerAPI, settingsAPI, unwrapList } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isFieldTech as isFieldTechUser } from '../../utils/isFieldTech';
import { cardStyle, workOrderCardStyle, eventCrewIds } from '../../utils/crewColors';
import { crewPickerOptions, crewIdentity } from '../../utils/crewIdentity';
import useSettingsList from '../../hooks/useSettingsList';
import DateRangeSelector from './DateRangeSelector';
import LocationCombobox from './LocationCombobox';
import { holidayName } from '../../utils/federalHolidays';
import { humanizeEnum } from '../../utils/humanize';
import { isDeactivatedStatus, deactivatedUserIds } from '../../utils/userStatus';
import { downloadIcs, parseIcs, mergeImportedEvents } from '../../utils/icsCalendar';
import { UndoToast } from '../ui/UndoToast';
import { useToast } from '../ui/Toast';
import { CalendarEventModal } from './CalendarEventModal';
import { detectConflicts, workOrderHasValidRange } from '../../utils/workOrderModel';
import { woDisplayNumber } from '../../utils/workOrderEdit';
import { woSchedule, woDays, formatWoChip, cardHoursOnDay, dayKey } from '../../utils/woSchedule';
import { ConflictWarnings } from '../workorders/ConflictWarnings';

// ─── Constants ───────────────────────────────────────────────────────────────

// Iter-18: view options are now inlined in the SelectMenu below (Month/Week/Day) + separate List toggle.

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ─── Utility Functions ───────────────────────────────────────────────────────

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const days = [];

  // Previous month padding
  for (let i = startPad - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }

  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }

  // Next month padding
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
  }

  return days;
}

function getWeekDays(date) {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

// Parse an event's date as a LOCAL date. Plain "YYYY-MM-DD" strings parse as
// UTC midnight via new Date(), which shifts to the previous day in negative-UTC
// timezones — so split and construct locally to keep day alignment correct.
function parseEventDate(ev) {
  const raw = ev.date || ev.dueDate || ev.scheduledDate || ev.start || ev.time;
  if (!raw) return new Date(NaN);
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(raw);
}

// IDI-475: does an event fall on `day`? A work-order card lists every local
// day it covers (`days`, from utils/woSchedule), so an Oct 7 -> Oct 9 WO shows
// on all three; everything else matches its start day as before.
function occursOn(ev, day) {
  const days = eventDays(ev);
  if (days) return days.includes(dayKey(day));
  return isSameDay(parseEventDate(ev), day);
}

// IDI-609 (D2): the local days an event covers, or null for a one-day event.
// A work-order card carries `days`; an appointment/task with an endDate after
// its date spans like a work order (woSchedule: backwards end ignored, 62-day
// cap). Everything else stays on its start day (IDI-613's rules).
function eventDays(ev) {
  if (Array.isArray(ev.days) && ev.days.length) return ev.days;
  if (ev.type === 'work_order' || ev.source === 'imported' || ev.type === 'imported') return null;
  const start = typeof ev.date === 'string' ? ev.date.slice(0, 10) : '';
  const end = typeof ev.endDate === 'string' ? ev.endDate.slice(0, 10) : '';
  if (!start || !end || end <= start) return null;
  const days = woDays({
    scheduledDate: start,
    endDate: end,
    startTime: ev.allDay ? undefined : ev.startTime || undefined,
    endTime: ev.allDay ? undefined : ev.endTime || undefined,
  });
  return days.length > 1 ? days : null;
}

// Hour an event occupies on the time grid. Prefer an explicit startTime
// ("HH:MM") over the date's hour (which is midnight for date-only events).
function eventStartHour(ev) {
  if (ev.startTime && /^\d{1,2}:/.test(ev.startTime)) {
    const [h, m] = ev.startTime.split(':').map(Number);
    return h + (m || 0) / 60;
  }
  const d = new Date(ev.time || ev.start || ev.date);
  const h = d.getHours();
  return Number.isNaN(h) ? 9 : h;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// IDI-616 (D2): the card's start label. Read the API's startTime ("HH:MM")
// the way eventStartHour does; the browser-only `time` is a fallback. All day: none.
function eventTimeLabel(ev) {
  if (ev.allDay) return '';
  if (ev.startTime && /^\d{1,2}:/.test(ev.startTime)) {
    const [h, m] = ev.startTime.split(':').map(Number);
    return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return formatTime(ev.time);
}

// End hour for duration-proportional rendering. Returns fractional hours.
function eventEndHour(ev) {
  if (ev.endTime && /^\d{1,2}:/.test(ev.endTime)) {
    const [h, m] = ev.endTime.split(':').map(Number);
    return h + (m || 0) / 60;
  }
  // Fallback: start + 1 hour
  return null;
}

// IDI-613 (D3): whole-day arithmetic on "YYYY-MM-DD" keys (local, DST-safe).
const ymdToLocal = (s) => {
  const m = String(s || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
function daysBetween(fromYmd, toYmd) {
  const a = ymdToLocal(fromYmd);
  const b = ymdToLocal(toYmd);
  if (!a || !b) return 0;
  return Math.round((b - a) / 86400000);
}
function addDaysYmd(ymd, n) {
  const d = ymdToLocal(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + n);
  return dayKey(d);
}
// A work-order card moved to newDate..newEnd keeping its own times: date,
// days and the chip label all follow (the server shifts the stop, IDI-466).
function shiftWoCard(card, newDate, newEnd) {
  const wo = {
    scheduledDate: newDate,
    startTime: card.startTime || undefined,
    endDate: newEnd,
    endTime: card.stopTime || card.endTime || undefined,
  };
  return {
    ...card,
    date: newDate,
    start: newDate,
    end: newEnd,
    days: woDays(wo),
    timeLabel: formatWoChip(wo),
  };
}

// IDI-475: the hours an event fills on ONE day of the time grid
// (utils/woSchedule.cardHoursOnDay). A multi-day WO runs from its start time
// on the first day, all day in between, and until its stop on the last day.
function hoursOnDay(ev, day) {
  const days = eventDays(ev);
  if (!days || days.length < 2) {
    const start = eventStartHour(ev);
    const end = eventEndHour(ev);
    // IDI-613 (D4): a same-day event that ends before it starts (10 PM-2 AM)
    // runs to midnight.
    if (end != null && end < start) return { startH: start, endH: 24 };
    return { startH: start, endH: end || start + 1 };
  }
  // IDI-609: an appointment span ends at its endTime on the last day.
  return cardHoursOnDay({ ...ev, days, stopTime: ev.stopTime || (ev.type === 'work_order' ? undefined : ev.endTime) }, dayKey(day));
}

// IDI-476: where a timed card sits on the week/day grid. Its top lines up
// with its start slot and its height is its range × the slot height; the
// only floor is half an hour so a very short event stays clickable.
const MIN_CARD_HOURS = 0.5;
export function timedCardBox(startH, endH, gridStartH, slotPx) {
  const duration = Math.max(endH - startH, MIN_CARD_HOURS);
  return { top: (startH - gridStartH) * slotPx, height: duration * slotPx };
}

// ─── Event Chip ──────────────────────────────────────────────────────────────

// IDI-476: the Settings → Crews list, so every chip (month, week, day, list)
// resolves a work order's crew colour the same way, without prop-drilling.
const CalendarCrewsContext = React.createContext([]);

/** Style for any calendar event: WOs by crew colour, everything else by type. */
export function eventCardStyle(event, crews) {
  if (event && event.type === 'work_order') return workOrderCardStyle(event, crews);
  // Appointments booked for a crew (IDI-476 crewId on the Lambda) take its colour too.
  // Only when the crew is still in Settings; otherwise the type style stands.
  if (event && event.crewId && (event.type || 'appointment') === 'appointment' && event.source !== 'imported'
      && crewIdentity({ crewId: event.crewId }, crews).resolved) {
    return workOrderCardStyle({ ...event, crewIds: undefined, crewColor: undefined }, crews);
  }
  return { ...cardStyle(event), crews: [], dots: false };
}

/** One colour dot per crew; replaces the type icon on a multi-crew card. */
function CrewDots({ crews, size = 'h-2 w-2' }) {
  return (
    <span className="flex items-center gap-0.5 flex-shrink-0" data-testid="crew-dots">
      {crews.map((c) => (
        <span
          key={c.id}
          data-testid="crew-dot"
          data-crew-id={c.id}
          title={c.name}
          aria-label={c.name}
          className={`${size} rounded-full`}
          style={{ backgroundColor: c.color, border: '1px solid rgba(255,255,255,0.7)' }}
        />
      ))}
    </span>
  );
}

function EventChip({ event, compact = false, onDragStart, resizable = false, onResizeStart, selected = false, onSelect }) {
  // IDI-476: work orders take their crew's Settings colour (one crew) or a
  // neutral block with one dot per crew (2+). Everything else: by EVENT TYPE.
  const settingsCrews = React.useContext(CalendarCrewsContext);
  const { backgroundColor, textColor, borderColor, isImported, Icon, crews: chipCrews, dots } = eventCardStyle(event, settingsCrews);
  // G3b: Completed/cancelled WOs are not draggable
  const woLocked = event.type === 'work_order' && ['completed', 'cancelled'].includes((event.status || '').toLowerCase());
  const draggable = !isImported && !woLocked;

  const handleDragStart = (e) => {
    if (!draggable) return;
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: event.id, type: 'calendar-event' }));
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStart) onDragStart(event);
  };

  // §3.5-5: resize handles (top + bottom) to adjust start/end time.
  // Imported events are view-only (no handles).
  const showResizeHandle = resizable && !isImported && selected;
  const handleResizeMouseDown = (e, edge) => {
    if (!showResizeHandle || !onResizeStart) return;
    e.stopPropagation();
    e.preventDefault();
    onResizeStart(event, e, edge);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(event);
  };

  if (compact) {
    return (
      <div
        className={`flex items-center gap-1 text-[10px] leading-tight px-1.5 py-0.5 rounded truncate font-medium cursor-grab h-full ${
          selected ? 'ring-2 ring-gray-900' : ''
        }`}
        style={{ backgroundColor, color: textColor, border: `1px solid ${borderColor}` }}
        data-testid="event-chip"
        data-card-bg={backgroundColor}
        title={event.title}
        draggable={draggable}
        onDragStart={handleDragStart}
        onClick={handleClick}
      >
        {dots ? <CrewDots crews={chipCrews} /> : <Icon className="h-2.5 w-2.5 flex-shrink-0" />}
        {/* IDI-475: week/month chips carry the time too (a span reads "Nov 7 8:00 AM – Nov 9"). */}
        {event.timeLabel && <span className="flex-shrink-0 opacity-75">{event.timeLabel}</span>}
        <span className="truncate">{event.title}</span>
        {/* Top resize handle */}
        {showResizeHandle && (
          <div
            className="absolute left-1/2 -translate-x-1/2 top-0 w-[30px] h-[6px] cursor-ns-resize flex items-center justify-center rounded-b bg-gray-800/80"
            onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
            onDragStart={(e) => e.preventDefault()}
          >
            <div className="w-3 h-0.5 rounded bg-white/80" />
          </div>
        )}
        {/* Bottom resize handle */}
        {showResizeHandle && (
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[30px] h-[6px] cursor-ns-resize flex items-center justify-center rounded-t bg-gray-800/80"
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
            onDragStart={(e) => e.preventDefault()}
          >
            <div className="w-3 h-0.5 rounded bg-white/80" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-start gap-1.5 px-2 py-1.5 rounded text-xs font-medium truncate cursor-grab h-full ${
        selected ? 'ring-2 ring-gray-900' : ''
      }`}
      style={{ backgroundColor, color: textColor, border: `1px solid ${borderColor}` }}
      data-testid="event-chip"
      data-card-bg={backgroundColor}
      title={event.title}
      draggable={draggable}
      onDragStart={handleDragStart}
      onClick={handleClick}
    >
      {dots ? <span className="mt-1"><CrewDots crews={chipCrews} size="h-2.5 w-2.5" /></span> : <Icon className="h-3 w-3 flex-shrink-0 mt-0.5" />}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate font-semibold">{event.title}</span>
        {/* IDI-475: a WO card carries its own label (start, or start – stop for spans). */}
        {event.timeLabel
          ? <span className="opacity-75 text-[10px]">{event.timeLabel}</span>
          : eventTimeLabel(event) && <span className="opacity-75 text-[10px]">{eventTimeLabel(event)}</span>}
        {isImported && <span className="opacity-75 text-[10px]">(imported)</span>}
      </div>
      {/* Top resize handle */}
      {showResizeHandle && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 w-[30px] h-[6px] cursor-ns-resize flex items-center justify-center rounded-b bg-gray-800/80"
          onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
          onDragStart={(e) => e.preventDefault()}
          data-resize-handle="true"
        >
          <div className="w-3 h-0.5 rounded bg-white/80" />
        </div>
      )}
      {/* Bottom resize handle */}
      {showResizeHandle && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[30px] h-[6px] cursor-ns-resize flex items-center justify-center rounded-b bg-gray-800/80"
          onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
          onDragStart={(e) => e.preventDefault()}
          data-resize-handle="true"
        >
          <div className="w-3 h-0.5 rounded bg-white/80" />
        </div>
      )}
    </div>
  );
}

// ─── Crew Legend Strip (R2 — CAL-3 State A) ─────────────────────────────────

function CrewLegend({ events }) {
  // Extract unique crews from visible events (IDI-476: every crew on a
  // multi-crew card, coloured from Settings like the cards themselves).
  const settingsCrews = React.useContext(CalendarCrewsContext);
  const crewMap = new Map();
  events.forEach(e => {
    const { crews: evCrews } = workOrderCardStyle({ ...e, type: 'work_order', source: undefined }, settingsCrews);
    evCrews.forEach((c) => {
      if (!crewMap.has(c.id)) crewMap.set(c.id, { name: c.name, color: c.color });
    });
  });
  if (crewMap.size === 0) return null;
  return (
    <div className="flex items-center gap-4 px-4 py-2 border-t border-[color:var(--color-border)] bg-gray-50/50">
      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Crews</span>
      {Array.from(crewMap.entries()).map(([id, { name, color }]) => (
        <div key={id} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
          <span className="text-xs text-gray-600">{name}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Month View ──────────────────────────────────────────────────────────────

function MonthView({ currentDate, events, onDateClick, onEventClick, onEventDrop, onDayDoubleClick }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const days = getMonthDays(year, month);
  const today = new Date();
  const [dragOverDate, setDragOverDate] = useState(null);

  const getEventsForDay = (date) => {
    return events.filter(e => occursOn(e, date));
  };

  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date.toISOString());
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = (e, date) => {
    e.preventDefault();
    setDragOverDate(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.type === 'calendar-event' && onEventDrop) {
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        onEventDrop(data.id, dateStr);
      }
    } catch (err) {
      // ignore invalid drag data
    }
  };

  return (
    <div className="flex-1 overflow-hidden">
      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-[color:var(--color-border)]">
        {DAYS_OF_WEEK.map(day => (
          <div key={day} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
            {day}
          </div>
        ))}
      </div>

      {/* Day Grid */}
      <div className="grid grid-cols-7 grid-rows-6 flex-1 h-[calc(100vh-220px)]">
        {days.map(({ date, isCurrentMonth }, idx) => {
          const dayEvents = getEventsForDay(date);
          const isToday = isSameDay(date, today);
          const holiday = holidayName(date);
          const isDragOver = dragOverDate === date.toISOString();

          return (
            <div
              key={idx}
              onDragOver={(e) => handleDragOver(e, date)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, date)}
              className={`border-b border-r border-[color:var(--color-border)] p-1 hover:bg-gray-50 transition-colors overflow-hidden ${
                !isCurrentMonth ? 'bg-gray-50/50' : ''
              } ${isDragOver ? 'bg-primary-50 ring-2 ring-inset ring-primary-300' : ''}`}
            >
              {/* Date number: single-click navigates to DayView */}
              <div className="flex justify-end mb-0.5 pr-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onDateClick(date); }}
                  className={`text-xs font-medium cursor-pointer hover:underline ${
                    isToday
                      ? 'h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center'
                      : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {date.getDate()}
                </button>
              </div>
              {/* Cell body: double-click opens create sheet */}
              <div
                className="min-h-[40px] cursor-pointer"
                onDoubleClick={() => onDayDoubleClick && onDayDoubleClick(date)}
              >
                {holiday && (
                  <div className="text-[9px] text-amber-600 truncate" title={holiday}>{holiday}</div>
                )}
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((event, eIdx) => (
                    /* IDI-537: a single click opens the event modal (Month,
                       Week and Day alike). Capture phase, because EventChip's
                       own click handler stops propagation (a bubble-phase
                       onClick here would never run). stopPropagation keeps the
                       click off the day cell; HTML5 drag never fires click, so
                       dragging still reschedules. */
                    <div
                      key={eIdx}
                      data-testid="month-event"
                      onClickCapture={() => onEventClick(event)}
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                    >
                      <EventChip event={event} compact />
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <p className="text-[10px] text-gray-500 pl-1">+{dayEvents.length - 3} more</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── All day row (IDI-613) ─────────────────────────────────────────────────────────
// Week and Day show all-day events (all-day appointments/tasks and work
// orders with no start time, D2) in a row above the hours, on every day they
// cover. Each day cell is a drop target that changes only the date.
const isAllDayEvent = (ev) => !!ev.allDay;
const ymdOfDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function AllDayRow({ days, events, labelClassName, onEventClick, onEventDrop }) {
  const [dragOver, setDragOver] = useState(null);
  const handleDrop = (e, day) => {
    e.preventDefault();
    setDragOver(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const dateStr = ymdOfDate(day);
      if (data.type === 'calendar-event' && onEventDrop) onEventDrop(data.id, dateStr, null);
    } catch (err) { /* ignore */ }
  };
  return (
    <div className="flex border-b border-[color:var(--color-border)] bg-white" data-testid="all-day-row">
      <div className={`${labelClassName} flex-shrink-0 border-r border-[color:var(--color-border)] pr-2 pt-1 text-right`}>
        <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>All day</span>
      </div>
      {days.map((day) => {
        const key = ymdOfDate(day);
        const dayEvts = events.filter(ev => isAllDayEvent(ev) && occursOn(ev, day));
        return (
          <div
            key={key}
            data-testid="all-day-cell"
            data-date={key}
            className={`flex-1 min-w-0 min-h-[28px] p-0.5 space-y-0.5 border-r border-[color:var(--color-border)] ${
              dragOver === key ? 'bg-primary-50 ring-2 ring-inset ring-primary-300' : ''
            }`}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(key); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => handleDrop(e, day)}
          >
            {dayEvts.map((event, eIdx) => (
              <div
                key={event.id || eIdx}
                data-testid="all-day-event"
                data-event-id={event.id}
                onClickCapture={() => onEventClick(event)}
              >
                <EventChip event={event} compact />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ─── Week View ───────────────────────────────────────────────────────────────

function WeekView({ currentDate, events, onEventClick, onEventDrop, onUnscheduledDrop, onTimeSlotDoubleClick, onDayHeaderDoubleClick, onEventResize, canResize = true }) {
  const weekDays = getWeekDays(currentDate);
  const today = new Date();
  const hours = Array.from({ length: 24 }, (_, i) => i); // 12am to 11pm (full day, scrollable)
  const WEEK_SLOT_PX = 48; // height per hour slot in week view
  const [dragOverCell, setDragOverCell] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [resizeGhost, setResizeGhost] = useState(null);
  const scrollRef = React.useRef(null);
  const resizingRef = React.useRef(false);

  // Scroll to 7am on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 7 * WEEK_SLOT_PX;
    }
  }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // R18: Resize handler for WeekView (same logic as DayView)
  const handleResizeStart = (event, e, edge = 'bottom') => {
    e.stopPropagation();
    resizingRef.current = true; // IDI-537: the release click must not open the pop-up
    const startY = e.clientY;
    const evStartHour = eventStartHour(event);
    const evEndHour = eventEndHour(event) || evStartHour + 1;
    let newStart = evStartHour;
    let newEnd = evEndHour;
    setResizeGhost({ eventId: event.id, edge, startHour: newStart, endHour: newEnd });

    const onMove = (moveEvent) => {
      const deltaPx = moveEvent.clientY - startY;
      const deltaHalfHours = Math.round(deltaPx / (WEEK_SLOT_PX / 2));
      const deltaHours = deltaHalfHours * 0.5;
      if (edge === 'bottom') {
        newEnd = Math.max(evStartHour + 0.5, evEndHour + deltaHours);
        newEnd = Math.min(newEnd, hours[hours.length - 1] + 1);
        newStart = evStartHour;
      } else {
        newStart = Math.min(evEndHour - 0.5, evStartHour + deltaHours);
        newStart = Math.max(newStart, hours[0]);
        newEnd = evEndHour;
      }
      setResizeGhost({ eventId: event.id, edge, startHour: newStart, endHour: newEnd });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setTimeout(() => { resizingRef.current = false; }, 0);
      setResizeGhost(null);
      if (onEventResize) onEventResize(event.id, newEnd, newStart);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleEventSelect = (event) => {
    setSelectedEventId(prev => prev === event.id ? null : event.id);
  };

  const getEventsForDay = (date) => {
    // IDI-613: all-day events live in the All day row, not the hour grid.
    return events.filter(e => occursOn(e, date) && !isAllDayEvent(e));
  };

  const handleDragOver = (e, day, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(`${day.toISOString()}-${hour}`);
  };

  const handleDrop = (e, day, hour) => {
    e.preventDefault();
    setDragOverCell(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
      const timeStr = `${String(hour).padStart(2, '0')}:00`;
      if (data.type === 'calendar-event' && onEventDrop) {
        onEventDrop(data.id, dateStr, timeStr);
      } else if (data.type === 'unscheduled-item' && onUnscheduledDrop) {
        onUnscheduledDrop(data.id, dateStr, timeStr);
      }
    } catch (err) { /* ignore */ }
  };

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto">
      {/* Day Headers */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[color:var(--color-border)] sticky top-0 bg-white z-20">
        <div className="border-r border-[color:var(--color-border)]" />
        {weekDays.map((day, idx) => {
          const isToday = isSameDay(day, today);
          return (
            <div
              key={idx}
              className="px-2 py-2 text-center border-r border-[color:var(--color-border)] cursor-pointer"
              onDoubleClick={() => onDayHeaderDoubleClick && onDayHeaderDoubleClick(day)}
            >
              <p className="text-xs text-gray-500">{DAYS_OF_WEEK[day.getDay()]}</p>
              <p className={`text-sm font-medium ${isToday ? 'text-red-600' : 'text-gray-900'}`}>
                {day.getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* IDI-613: All day row above the hours */}
      <AllDayRow
        days={weekDays}
        events={events}
        labelClassName="w-[60px]"
        onEventClick={onEventClick}
        onEventDrop={onEventDrop}
      />

      {/* Time Grid — absolute positioning for duration-proportional cards */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)]" style={{ minHeight: hours.length * WEEK_SLOT_PX }}>
        {/* Hour labels column */}
        <div className="relative" style={{ height: hours.length * WEEK_SLOT_PX }}>
          {hours.map(hour => (
            <div
              key={hour}
              className="absolute left-0 right-0 border-r border-[color:var(--color-border)] pr-2 pt-1 text-right"
              style={{ top: (hour - hours[0]) * WEEK_SLOT_PX, height: WEEK_SLOT_PX }}
            >
              <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                {hour > 12 ? `${hour - 12}pm` : hour === 12 ? '12pm' : `${hour}am`}
              </span>
            </div>
          ))}
        </div>
        {/* Day columns with absolute-positioned events */}
        {weekDays.map((day, dayIdx) => {
          const dayEvts = getEventsForDay(day);
          return (
            <div key={dayIdx} className="relative border-r border-[color:var(--color-border)]" style={{ height: hours.length * WEEK_SLOT_PX }}>
              {/* Hour grid lines + drop targets */}
              {hours.map(hour => {
                const cellKey = `${day.toISOString()}-${hour}`;
                return (
                  <div
                    key={hour}
                    className={`absolute left-0 right-0 border-b border-[color:var(--color-border)] ${
                      dragOverCell === cellKey ? 'bg-primary-50 ring-2 ring-inset ring-primary-300' : ''
                    }`}
                    style={{ top: (hour - hours[0]) * WEEK_SLOT_PX, height: WEEK_SLOT_PX }}
                    onDragOver={(e) => handleDragOver(e, day, hour)}
                    onDragLeave={() => setDragOverCell(null)}
                    onDrop={(e) => handleDrop(e, day, hour)}
                    onDoubleClick={() => onTimeSlotDoubleClick && onTimeSlotDoubleClick(day, hour)}
                  />
                );
              })}
              {/* Events — side-by-side for overlapping */}
              {(() => {
                const items = dayEvts.map(event => {
                  const { startH, endH } = hoursOnDay(event, day);
                  return { event, startH, endH, col: 0, totalCols: 1 };
                });
                // Assign columns for overlapping events
                for (let i = 0; i < items.length; i++) {
                  const group = [items[i]];
                  for (let j = i + 1; j < items.length; j++) {
                    if (items[j].startH < items[i].endH && items[j].endH > items[i].startH) {
                      group.push(items[j]);
                    }
                  }
                  if (group.length > 1) {
                    group.forEach((item, idx) => {
                      item.col = idx;
                      item.totalCols = Math.max(item.totalCols, group.length);
                    });
                  }
                }
                return items.map(({ event, startH, endH, col, totalCols }, eIdx) => {
                  const { top, height } = timedCardBox(startH, endH, hours[0], WEEK_SLOT_PX);
                  const widthPct = 100 / totalCols;
                  const leftPct = col * widthPct;
                  return (
                    <div
                      key={event.id || eIdx}
                      data-testid="timed-card"
                      data-event-id={event.id}
                      className="absolute z-10 cursor-pointer"
                      style={{ top, height, left: `${leftPct}%`, width: `${widthPct}%`, paddingLeft: 2, paddingRight: 2 }}
                      onClickCapture={() => { if (!resizingRef.current) onEventClick(event); }}
                      onDoubleClick={(e) => e.stopPropagation()}
                    >
                      <EventChip
                        event={event}
                        compact
                        resizable={canResize}
                        onResizeStart={handleResizeStart}
                        selected={selectedEventId === event.id}
                        onSelect={handleEventSelect}
                      />
                    </div>
                  );
                });
              })()}
              {/* R9: Red dashed ghost during resize */}
              {resizeGhost && resizeGhost.eventId && dayEvts.some(ev => ev.id === resizeGhost.eventId) && (() => {
                const ghostTop = (resizeGhost.startHour - hours[0]) * WEEK_SLOT_PX;
                const ghostHeight = (resizeGhost.endHour - resizeGhost.startHour) * WEEK_SLOT_PX;
                return (
                  <div
                    className="absolute left-0.5 right-0.5 pointer-events-none z-20"
                    style={{
                      top: ghostTop,
                      height: ghostHeight,
                      border: '2px dashed var(--color-error)',
                      background: 'color-mix(in srgb, var(--color-error) 5%, white)',
                      borderRadius: 4,
                    }}
                  />
                );
              })()}
            </div>
          );
        })}
      </div>
      <CrewLegend events={events} />
    </div>
  );
}

// ─── Day View ────────────────────────────────────────────────────────────────

function DayView({ currentDate, events, onEventClick, onEventDrop, onEventResize, onUnscheduledDrop, onTimeSlotDoubleClick, canResize = true }) {
  const SLOT_PX = 60; // matches min-h-[60px] hour rows
  const hours = Array.from({ length: 24 }, (_, i) => i); // 12am to 11pm (full day, scrollable)
  const [dragOverHour, setDragOverHour] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const scrollRef = React.useRef(null);
  const resizingRef = React.useRef(false);

  // Scroll to 7am on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 7 * SLOT_PX;
    }
  }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // R8-R12: Edge-resize with 30-min snap, red dashed ghost, both edges.
  const [resizeGhost, setResizeGhost] = useState(null); // { eventId, edge, startHour, endHour }
  const handleResizeStart = (event, e, edge = 'bottom') => {
    e.stopPropagation();
    resizingRef.current = true; // IDI-537: the release click must not open the pop-up
    const startY = e.clientY;
    const evStartHour = eventStartHour(event);
    const evEndHour = eventEndHour(event) || evStartHour + 1;
    let newStart = evStartHour;
    let newEnd = evEndHour;
    setResizeGhost({ eventId: event.id, edge, startHour: newStart, endHour: newEnd });

    const onMove = (moveEvent) => {
      const deltaPx = moveEvent.clientY - startY;
      const deltaHalfHours = Math.round(deltaPx / (SLOT_PX / 2)); // 30-min snap
      const deltaHours = deltaHalfHours * 0.5;
      if (edge === 'bottom') {
        newEnd = Math.max(evStartHour + 0.5, evEndHour + deltaHours); // min 30-min duration
        newEnd = Math.min(newEnd, hours[hours.length - 1] + 1); // clamp to grid end
        newStart = evStartHour;
      } else {
        newStart = Math.min(evEndHour - 0.5, evStartHour + deltaHours); // min 30-min duration
        newStart = Math.max(newStart, hours[0]); // clamp to grid start
        newEnd = evEndHour;
      }
      setResizeGhost({ eventId: event.id, edge, startHour: newStart, endHour: newEnd });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setTimeout(() => { resizingRef.current = false; }, 0);
      setResizeGhost(null);
      if (onEventResize) {
        onEventResize(event.id, newEnd, newStart);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleEventSelect = (event) => {
    setSelectedEventId(prev => prev === event.id ? null : event.id);
  };
  const dayEvents = events.filter(e => occursOn(e, currentDate));
  // IDI-613: all-day events sit in the All day row, not the hour grid.
  const timedDayEvents = dayEvents.filter(e => !isAllDayEvent(e));

  const handleDragOver = (e, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverHour(hour);
  };

  const handleDrop = (e, hour) => {
    e.preventDefault();
    setDragOverHour(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const timeStr = `${String(hour).padStart(2, '0')}:00`;
      if (data.type === 'calendar-event' && onEventDrop) {
        onEventDrop(data.id, dateStr, timeStr);
      } else if (data.type === 'unscheduled-item' && onUnscheduledDrop) {
        onUnscheduledDrop(data.id, dateStr, timeStr);
      }
    } catch (err) { /* ignore */ }
  };

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto">
      {/* Day Header */}
      <div className="border-b border-[color:var(--color-border)] px-4 py-3 sticky top-0 bg-white z-20">
        <p className="text-lg font-semibold text-gray-900">
          {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
        </p>
        <p className="text-sm text-gray-500">{dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}</p>
      </div>

      {/* IDI-613: All day row above the hours */}
      <AllDayRow
        days={[currentDate]}
        events={events}
        labelClassName="w-16"
        onEventClick={onEventClick}
        onEventDrop={onEventDrop}
      />

      {/* Time Slots */}
      <div className="relative" style={{ height: hours.length * SLOT_PX }}>
        {/* Hour grid lines */}
        {hours.map(hour => (
          <div
            key={hour}
            className="absolute left-0 right-0 flex"
            style={{ top: (hour - hours[0]) * SLOT_PX, height: SLOT_PX }}
          >
            <div className="w-16 flex-shrink-0 pr-2 pt-1 text-right">
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {hour > 12 ? `${hour - 12}pm` : hour === 12 ? '12pm' : `${hour}am`}
              </span>
            </div>
            <div
              className={`flex-1 border-l border-b border-[color:var(--color-border)] ${
                dragOverHour === hour ? 'bg-primary-50 ring-2 ring-inset ring-primary-300' : ''
              }`}
              onDragOver={(e) => handleDragOver(e, hour)}
              onDragLeave={() => setDragOverHour(null)}
              onDrop={(e) => handleDrop(e, hour)}
              onDoubleClick={() => onTimeSlotDoubleClick && onTimeSlotDoubleClick(currentDate, hour)}
            />
          </div>
        ))}
        {/* Duration-proportional event cards */}
        {/* Duration-proportional event cards with overlap handling */}
        {(() => {
          // Calculate overlap columns for side-by-side layout
          const positioned = timedDayEvents.map(event => {
            const { startH, endH } = hoursOnDay(event, currentDate);
            return { event, startH, endH };
          });
          // Assign columns: events that overlap get placed side-by-side
          positioned.forEach((item, i) => {
            item.col = 0;
            item.totalCols = 1;
          });
          for (let i = 0; i < positioned.length; i++) {
            const group = [positioned[i]];
            for (let j = i + 1; j < positioned.length; j++) {
              if (positioned[j].startH < positioned[i].endH && positioned[j].endH > positioned[i].startH) {
                group.push(positioned[j]);
              }
            }
            if (group.length > 1) {
              group.forEach((item, idx) => {
                item.col = idx;
                item.totalCols = Math.max(item.totalCols, group.length);
              });
            }
          }
          return positioned.map(({ event, startH, endH, col, totalCols }, eIdx) => {
            const { top, height } = timedCardBox(startH, endH, hours[0], SLOT_PX);
            return (
              <div
                key={event.id || eIdx}
                data-testid="timed-card"
                data-event-id={event.id}
                className="absolute cursor-pointer"
                style={{ top, height, left: `calc(72px + (100% - 80px) * ${col / totalCols})`, width: `calc((100% - 80px) / ${totalCols})` }}
                onClickCapture={() => { if (!resizingRef.current) onEventClick(event); }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                <EventChip
                  event={event}
                  resizable={canResize}
                  onResizeStart={handleResizeStart}
                  selected={selectedEventId === event.id}
                  onSelect={handleEventSelect}
                />
              </div>
            );
          });
        })()}
        {/* R9: Red dashed ghost during resize */}
        {resizeGhost && (() => {
          const ghostTop = (resizeGhost.startHour - hours[0]) * SLOT_PX;
          const ghostHeight = (resizeGhost.endHour - resizeGhost.startHour) * SLOT_PX;
          const timeLabel = (() => {
            const h = resizeGhost.edge === 'bottom' ? resizeGhost.endHour : resizeGhost.startHour;
            const hrs = Math.floor(h);
            const mins = Math.round((h - hrs) * 60);
            const ampm = hrs >= 12 ? 'PM' : 'AM';
            const display = hrs > 12 ? hrs - 12 : (hrs === 0 ? 12 : hrs);
            return `${display}:${String(mins).padStart(2, '0')} ${ampm}`;
          })();
          return (
            <div
              className="absolute pointer-events-none z-20"
              style={{
                top: ghostTop,
                height: ghostHeight,
                left: 72,
                right: 8,
                border: '2px dashed var(--color-error)',
                background: 'color-mix(in srgb, var(--color-error) 5%, white)',
                borderRadius: 6,
              }}
            >
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap">
                {resizeGhost.edge === 'bottom' ? `new end ${timeLabel}` : `new start ${timeLabel}`}
              </div>
            </div>
          );
        })()}
      </div>
      <CrewLegend events={dayEvents} />
    </div>
  );
}

// ─── List View ───────────────────────────────────────────────────────────────

function ListView({ currentDate, events, onEventClick }) {
  const settingsCrews = React.useContext(CalendarCrewsContext);
  // IDI-476: a WO's stripe is its crew colour (neutral when several crews).
  const stripeColor = (event) => {
    if (event.type === 'work_order') {
      const st = workOrderCardStyle(event, settingsCrews);
      if (st.crews.length) return st.backgroundColor;
    }
    return event.type === 'task' ? 'var(--red-200)' : event.type === 'imported' ? 'var(--neutral-200)' : 'var(--neutral-400)';
  };
  // Group events by date for the current week
  const weekDays = getWeekDays(currentDate);

  const groupedEvents = weekDays.map(day => ({
    date: day,
    events: events.filter(e => occursOn(e, day)),
  })).filter(g => g.events.length > 0);

  if (groupedEvents.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <CalendarDaysIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No events this week</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-6">
      {groupedEvents.map(({ date, events: dayEvents }) => (
        <div key={date.toISOString()}>
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            {date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </h3>
          <div className="space-y-2">
            {dayEvents.map((event, idx) => (
              <div
                key={idx}
                onClick={() => onEventClick(event)}
                className="flex items-center gap-3 card px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div
                  className="w-1 h-10 rounded-full flex-shrink-0"
                  style={{ backgroundColor: stripeColor(event) }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {(event.timeLabel || eventTimeLabel(event)) && (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-3 w-3" />
                        {event.timeLabel || eventTimeLabel(event)}
                      </span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPinIcon className="h-3 w-3" />
                        {event.location}
                      </span>
                    )}
                  </div>
                </div>
                <Chip color={event.type === 'task' ? 'default' : event.type === 'appointment' ? 'info' : 'default'}>
                  {humanizeEnum(event.type) || 'Event'}
                </Chip>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Unscheduled Panel ───────────────────────────────────────────────────────

// Iter-18: retained during transition; the calendar now uses a real Slideover for Unscheduled.
// eslint-disable-next-line no-unused-vars
function UnscheduledPanel({ items, open, onToggle }) {
  if (!open) return null;

  return (
    <div className="w-72 border-l border-[color:var(--color-border)] bg-white overflow-y-auto flex-shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--color-border)]">
        <h3 className="text-sm font-medium text-gray-900">Unscheduled</h3>
        <span className="text-xs text-gray-500">{items.length}</span>
      </div>
      <div className="p-3 space-y-2">
        {items.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">All items are scheduled</p>
        ) : (
          items.map((item, idx) => {
            const itemId = item.id || item.jobId || item.workOrderId;
            const handleDragStart = (e) => {
              // §3.5-4: drag-to-assign payload consumed by Day/Week drop handlers
              e.dataTransfer.setData('text/plain', JSON.stringify({ id: itemId, type: 'unscheduled-item' }));
              e.dataTransfer.effectAllowed = 'move';
            };
            return (
              <div
                key={itemId || idx}
                draggable
                onDragStart={handleDragStart}
                className="card px-3 py-2 cursor-grab hover:border-[color:var(--color-border)] transition"
                title="Drag onto a calendar day to schedule"
              >
                <p className="text-xs font-medium text-gray-900 truncate">{item.title}</p>
                <p className="text-[10px] text-gray-500">{item.customerName || item.type}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Create Dropdown ─────────────────────────────────────────────────────────

function CreateDropdown({ onSelect }) {
  const [open, setOpen] = useState(false);

  const options = [
    // Iter-18 CAL wire: Bid removed from Create menu (only Appointment / Task).
    { id: 'appointment', label: 'Appointment', icon: <CalendarDaysIcon className="h-4 w-4" /> },
    { id: 'task', label: 'Task', icon: <CheckIcon className="h-4 w-4" /> },
  ];

  return (
    <div className="relative">
      <Button onClick={() => setOpen(!open)}>
        <PlusIcon className="h-4 w-4 mr-1" />
        Create
        <ChevronDownIcon className="h-3.5 w-3.5 ml-1" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-48 card py-1">
            {options.map(opt => (
              <button
                key={opt.id}
                onClick={() => { onSelect(opt.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <span>{opt.icon}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── View memory (IDI-476) ──────────────────────────────────────────────────
// The view (month/week/day) and the calendar/list mode are read from the URL
// first, then this user's saved preference, then month/calendar. Every change
// the user makes is written to both, so leaving in week view and coming back
// (or reloading) opens week view. Keys are per user, so two people on one
// browser keep their own default.
const CALENDAR_VIEWS = ['month', 'week', 'day'];
const CALENDAR_MODES = ['calendar', 'list'];
export const calendarViewStorageKey = (userId) => `bidit_calendar_view:${userId || 'anon'}`;

function readStoredCalendarView(userId) {
  try {
    const raw = window.localStorage.getItem(calendarViewStorageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      view: CALENDAR_VIEWS.includes(parsed?.view) ? parsed.view : undefined,
      mode: CALENDAR_MODES.includes(parsed?.mode) ? parsed.mode : undefined,
    };
  } catch {
    return {};
  }
}

function writeStoredCalendarView(userId, view, mode) {
  try {
    window.localStorage.setItem(calendarViewStorageKey(userId), JSON.stringify({ view, mode }));
  } catch {
    // Storage full or blocked (private mode): the URL still carries the view.
  }
}

/** URL `?view=` / `?mode=` (legacy `?view=list` = list mode); undefined when absent or invalid. */
function calendarViewFromParams(searchParams) {
  const v = searchParams.get('view');
  const m = searchParams.get('mode');
  return {
    view: CALENDAR_VIEWS.includes(v) ? v : undefined,
    mode: CALENDAR_MODES.includes(m) ? m : (v === 'list' ? 'list' : undefined),
  };
}

export function initialCalendarView(searchParams, userId) {
  const fromUrl = calendarViewFromParams(searchParams);
  const stored = readStoredCalendarView(userId);
  return {
    view: fromUrl.view || stored.view || 'month',
    // A link that names a grid view (e.g. the dashboard's `?view=day`) means
    // the grid, not a remembered list mode.
    mode: fromUrl.mode || (fromUrl.view ? 'calendar' : stored.mode) || 'calendar',
  };
}

// ─── Main Calendar Component ─────────────────────────────────────────────────

export default function CalendarPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  // IDI-598 D3: the shared field-tech rule (groups first, else role).
  const isFieldTech = isFieldTechUser(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewUserId = user?.id || user?.sub || user?.userId || user?.email || '';
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsed = new Date(dateParam + 'T00:00:00');
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  // IDI-476: remembered per user (URL → localStorage → month). `view` keeps
  // its old meaning ('list' when in list mode) for the rest of the page.
  const [viewPref, setViewPref] = useState(() => initialCalendarView(searchParams, viewUserId));
  const calView = viewPref.view;
  const view = viewPref.mode === 'list' ? 'list' : calView;
  const setCalendarView = (nextView, nextMode) => {
    const next = {
      view: CALENDAR_VIEWS.includes(nextView) ? nextView : calView,
      mode: CALENDAR_MODES.includes(nextMode) ? nextMode : viewPref.mode,
    };
    setViewPref(next);
    writeStoredCalendarView(viewUserId, next.view, next.mode);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('view', next.view);
      params.set('mode', next.mode);
      return params;
    }, { replace: true });
  };
  // The signed-in user can arrive after first render: load their saved view
  // then, unless the URL named one.
  const loadedViewUserRef = React.useRef(viewUserId);
  useEffect(() => {
    if (loadedViewUserRef.current === viewUserId) return;
    loadedViewUserRef.current = viewUserId;
    setViewPref(initialCalendarView(searchParams, viewUserId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId]);
  // IDI-613 (D5): the URL can change under the page (a Month drill-in's
  // ?view=day, the sidebar's bare /calendar). Follow it: a named view wins,
  // otherwise the remembered one.
  const urlView = searchParams.get('view');
  const urlMode = searchParams.get('mode');
  const lastUrlViewRef = React.useRef(`${urlView}|${urlMode}`);
  useEffect(() => {
    const key = `${urlView}|${urlMode}`;
    if (lastUrlViewRef.current === key) return;
    lastUrlViewRef.current = key;
    setViewPref(initialCalendarView(searchParams, viewUserId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlView, urlMode]);
  const [events, setEvents] = useState([]);
  // PR-42 §2c: distinguish ERROR from EMPTY. Nullish = fetch never
  // failed (loading or genuinely empty). Non-null = fetch rejected;
  // ErrorState renders above the grid. IDI-60 was unresolvable while
  // this was a swallowed [].
  const [loadError, setLoadError] = useState(null);
  const [unscheduledItems, setUnscheduledItems] = useState([]);
  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [_loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
  // IDI-476: Settings → Crews, the one source of a card's crew colour.
  const { items: settingsCrewItems } = useSettingsList('crews');
  const settingsCrews = Array.isArray(settingsCrewItems) ? settingsCrewItems : [];
  const [crewFilter] = useState(''); // Iter-18: setter removed with All-Crews control; filter still applied if state changes elsewhere
  const [projectSearch] = useState(''); // Iter-18: setter removed with Search input; filter still applied if state changes elsewhere
  const fileInputRef = React.useRef(null);

  // Side-sheet state — unified create sheet with type toggle
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [createSheetType, setCreateSheetType] = useState('appointment');
  const [createSheetDefaults, setCreateSheetDefaults] = useState({});

  // Legacy compat — keep old state names wired to unified sheet
  const setShowAppointmentSheet = (v) => { if (v) { setCreateSheetType('appointment'); setCreateSheetDefaults({}); setShowCreateSheet(true); } else { setShowCreateSheet(false); } };
  const setShowTaskSheet = (v) => { if (v) { setCreateSheetType('task'); setCreateSheetDefaults({}); setShowCreateSheet(true); } else { setShowCreateSheet(false); } };

  // Drag-and-drop + undo state (§3.5-5)
  const [undoToast, setUndoToast] = useState(null);

  // Conflict warnings state (§4.7)
  const [dragConflicts, setDragConflicts] = useState([]);

  // Event modal state (§3.5-2)
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, view]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      // Fetch calendar events from API
      // PR-50 (2026-08-18, 344_ §5 60b): the inner try/catch that lived here
      // silently swallowed /v1/calendar/events rejections into data=[]. PR-42
      // added an OUTER catch (see the wrapping try below) that sets loadError
      // and renders ErrorState — but the inner catch prevented the outer
      // from ever seeing a calendar-events failure. Now the failure propagates,
      // the outer catch fires, and the user sees a distinct ERROR state
      // instead of an empty calendar for both "genuinely nothing scheduled"
      // AND "the endpoint is broken."
      //
      // 344_ §5: this closes the frontend half (60b). 60a (endpoint 503s)
      // remains Tyler's; but 60b makes the failure LEGIBLE while 60a is fixed.
      const data = await api.getCalendarEvents({
        // IDI-615 D1/D3: the view's local days as YYYY-MM-DD (not UTC instants).
        start: ymdOfDate(getViewStart()),
        end: ymdOfDate(getViewEnd()),
      });
      const calendarEvents = Array.isArray(data) ? data : data?.events || [];
      // Iter-18: unscheduled panel is now sourced from Jobs (not_scheduled + partially_scheduled)
      // per operator direction (was: calendarAPI.unscheduled which was empty for most tenants).
      try {
        // IDI-141: replace manual reader (which looked at `.data`, never `.jobs`)
        // with `unwrapList` so the wrapped `{jobs, has_more, next_cursor}` envelope
        // from the newly capped endpoint is read correctly. Pre-fix, the manual
        // reader would silently return [] and BOTH the unscheduled panel AND the
        // WO calendar projection would render empty on this screen.
        const jobsRes = await jobAPI.getJobs();
        const { list: jobs } = unwrapList(jobsRes, 'jobs', 'CalendarPage');
        const unsched = jobs
          .filter(j => {
            const st = (j.status || '').toLowerCase();
            return st === 'not_scheduled' || st === 'partially_scheduled' || st === 'unscheduled';
          })
          .map(j => ({
            id: j.id,
            jobId: j.id,
            title: j.title || j.name || 'Untitled job',
            customerName: j.customer_name || j.customerName || '',
            type: j.jobType || j.job_type || 'job',
            status: j.status,
          }));
        setUnscheduledItems(unsched);

        // G3: Project WO cards onto calendar from jobs' work_orders with scheduled_date
        const woCards = [];
        jobs.forEach(j => {
          const wos = j.work_orders || j.workOrders || [];
          wos.forEach(wo => {
            // IDI-475: one reader for start + stop (utils/woSchedule). A bare
            // "YYYY-MM-DD" is LOCAL, an ISO stamp is an instant, and the stop
            // has its own date -- so times match the WO and spans cover every day.
            const sched = woSchedule(wo);
            if (!sched.start) return;
            const woStatus = (wo.status || '').toLowerCase();
            if (woStatus === 'cancelled') return;
            woCards.push({
              id: wo.id || `wo-${j.id}-${wo.wo_number || wo.woNumber}`,
              // IDI-470: never the raw `wo-…` id.
              title: (wo.title && wo.title.trim()) || wo.name || woDisplayNumber(wo) || 'Work order',
              date: sched.startDate,
              start: sched.startDate,
              end: sched.stopDate || sched.startDate,
              days: woDays(wo),
              startTime: sched.startTime || undefined,
              endTime: sched.multiDay ? undefined : (sched.stopTime || undefined),
              stopTime: sched.stopTime || undefined,
              timeLabel: formatWoChip(wo),
              type: 'work_order',
              // IDI-476: the card's crews. `crewIds` (IDI-503: several crews per
              // WO) wins; a WO with only `crewId` is one crew. Name/colour are
              // snapshots, used only when the crew is gone from Settings.
              crewId: wo.crewId || wo.crew_id || undefined,
              crewIds: eventCrewIds({
                crewIds: wo.crewIds || wo.crew_ids,
                crewId: wo.crewId || wo.crew_id,
              }),
              crewName: wo.crewName || wo.crew || undefined,
              crewColor: wo.crewColor || undefined,
              jobId: j.id,
              jobTitle: j.title || j.name || '',
              customerName: j.customer_name || j.customerName || '',
              status: wo.status,
              allDay: !sched.hasStartTime,
            });
          });
        });
        // Merge calendar events + WO cards in a single setEvents (avoids race condition)
        const existingIds = new Set(calendarEvents.map(e => e.id));
        const newWos = woCards.filter(w => !existingIds.has(w.id));
        setEvents([...calendarEvents, ...newWos]);
        setLoadError(null); // PR-42: success clears any prior error
      } catch (jerr) {
        console.error('Failed to fetch jobs for Unscheduled panel:', jerr);
        setUnscheduledItems([]);
        // Still show calendar events even if jobs fetch fails
        setEvents(calendarEvents);
      }
    } catch (err) {
      console.error('Failed to fetch calendar events:', err);
      setEvents([]);
      setLoadError(err); // PR-42 §2c: ERROR is now distinct from EMPTY
    } finally {
      setLoading(false);
    }
  };

  const getViewStart = () => {
    const d = new Date(currentDate);
    if (view === 'month') {
      // IDI-752 D1: from the grid's first cell, so the grey days get their events.
      return getMonthDays(d.getFullYear(), d.getMonth())[0].date;
    } else if (view === 'week' || view === 'list') {
      d.setDate(d.getDate() - d.getDay());
    }
    return d;
  };

  const getViewEnd = () => {
    const d = new Date(currentDate);
    if (view === 'month') {
      // IDI-752 D1: to the grid's last cell (always the 42nd).
      const days = getMonthDays(d.getFullYear(), d.getMonth());
      return days[days.length - 1].date;
    } else if (view === 'week' || view === 'list') {
      d.setDate(d.getDate() + (6 - d.getDay()));
    }
    return d;
  };

  const navigateDate = (direction) => {
    const d = new Date(currentDate);
    if (view === 'month') {
      d.setMonth(d.getMonth() + direction);
    } else if (view === 'week' || view === 'list') {
      d.setDate(d.getDate() + (direction * 7));
    } else {
      d.setDate(d.getDate() + direction);
    }
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  const handleDateClick = (date) => {
    setCurrentDate(date);
    if (view === 'month') {
      // IDI-613 (D5): a drill-in changes only the URL, like a ?view=day link;
      // the remembered view stays what the user picked.
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('view', 'day');
        params.set('mode', 'calendar');
        params.set('date', ymdOfDate(date));
        return params;
      });
    }
  };

  // R4: Double-click on time slot → open create sheet with date+time pre-filled
  const handleTimeSlotDoubleClick = (date, hour) => {
    // IDI-617 D3: field techs' calendar is view-only.
    if (isFieldTech) return;
    const roundedHour = Math.floor(hour);
    const mins = (hour - roundedHour) >= 0.5 ? '30' : '00';
    const startTime = `${String(roundedHour).padStart(2, '0')}:${mins}`;
    const endHour = mins === '30' ? roundedHour + 1 : roundedHour;
    const endMins = mins === '30' ? '00' : '30';
    const endTime = `${String(endHour).padStart(2, '0')}:${endMins}`;
    setCreateSheetType('appointment');
    setCreateSheetDefaults({
      date: date instanceof Date ? ymdOfDate(date) : date,
      startTime,
      endTime,
    });
    setShowCreateSheet(true);
  };

  // R5: Double-click on day header/cell → open create sheet with date only
  const handleDayDoubleClick = (date) => {
    if (isFieldTech) return; // IDI-617 D3
    setCreateSheetType('appointment');
    setCreateSheetDefaults({
      date: date instanceof Date ? ymdOfDate(date) : date,
    });
    setShowCreateSheet(true);
  };

  const handleEventClick = (event) => {
    // §3.5-2: Click opens quick-view modal (no navigation)
    setSelectedEvent(event);
  };

  // §3.5-5: Drag-and-drop reschedule with undo toast
  const handleEventDrop = async (eventId, newDate, newTime) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    // G3b: Field techs cannot reschedule
    if (isFieldTech) return;

    // PT-40.5 (2026-07-27): validate the proposed range BEFORE any optimistic
    // mutation. `workOrderHasValidRange` accepts both {startDate, endDate} and
    // {start_date, end_date} shapes; here we build the proposed shape from
    // the new drop target. Bail visibly on invalid range so the user sees why
    // the drop was rejected instead of a silent revert-later.
    // Save original for undo
    const originalDate = event.date || (event.time ? event.time.split('T')[0] : '');
    const originalTime = event.startTime || (event.time ? event.time.split('T')[1]?.slice(0, 5) : '');
    const isWo = event.type === 'work_order';

    // IDI-613 (D3): a drop shifts start AND stop by the same number of days,
    // so a later-day drop keeps the span instead of failing the range check.
    const delta = daysBetween(originalDate, newDate);
    const originalEnd = event.end || event.endDate || event.end_date || '';
    const proposedRange = {
      startDate: newDate,
      endDate: originalEnd ? addDaysYmd(originalEnd, delta) : newDate,
    };
    if (!workOrderHasValidRange(proposedRange)) {
      toast.error('Cannot reschedule: invalid date range');
      return;
    }
    // A drop never switches timed <-> all-day, and never changes a WO's times.
    const applyTime = newTime && !isWo && !event.allDay ? newTime : null;

    // Optimistic update: card, days and label move at once.
    setEvents(prev => prev.map(e => {
      if (e.id !== eventId) return e;
      if (isWo) return shiftWoCard(e, newDate, proposedRange.endDate);
      const updated = { ...e, date: newDate };
      // IDI-609 (D3): a span moves whole; the server shifts the end too.
      if (e.endDate && !e.days) updated.endDate = addDaysYmd(e.endDate, delta);
      if (applyTime) updated.startTime = applyTime;
      if (e.time) updated.time = `${newDate}T${applyTime || originalTime || '08:00'}:00`;
      return updated;
    }));

    // §4.7: Check for conflicts after move
    const proposed = {
      startDate: newDate,
      endDate: newDate,
      techIds: event.techIds || [],
      crewIds: event.crewId ? [event.crewId] : (event.crew_id ? [event.crew_id] : []),
    };
    const otherEvents = events.filter(e => e.id !== eventId).map(e => ({
      startDate: e.date || (e.time ? e.time.split('T')[0] : ''),
      endDate: e.date || (e.time ? e.time.split('T')[0] : ''),
      techIds: e.techIds || [],
      crewIds: e.crewId ? [e.crewId] : (e.crew_id ? [e.crew_id] : []),
    }));
    const conflicts = detectConflicts(proposed, { existing: otherEvents });
    setDragConflicts(conflicts);
    if (conflicts.length > 0) {
      setTimeout(() => setDragConflicts([]), 5000); // auto-dismiss after 5s
    }

    // Show undo toast
    setUndoToast({
      message: `"${event.title}" moved to ${newDate}`,
      onUndo: () => {
        // Revert optimistic update
        setEvents(prev => prev.map(e => (e.id === eventId ? event : e)));
        setUndoToast(null);
      },
      onDismiss: async () => {
        // Persist the change
        try {
          if (event.type === 'work_order' && event.jobId) {
            // G3b: WO reschedule — update via job endpoint
            await api.updateJob(event.jobId, {
              action: 'reschedule_work_order',
              work_order_id: event.id,
              scheduled_date: newDate,
            });
          } else {
            await calendarAPI.updateEvent(eventId, event.allDay
              ? { date: newDate }
              : { date: newDate, startTime: applyTime || originalTime });
          }
        } catch (err) {
          console.error('Failed to persist event move:', err);
          // PT-40.5 (2026-07-27): surface the failure to the user — previous
          // code silently reverted, which looked like a phantom no-op. Show
          // a real error toast so the operator knows the server rejected the
          // change (and, crucially, the calendar's visual state stays
          // consistent with what's actually persisted).
          toast.error(
            event.type === 'work_order'
              ? 'Failed to reschedule work order — change was not saved'
              : 'Failed to save event move',
          );
          // Revert on failure
          setEvents(prev => prev.map(e => (e.id === eventId ? event : e)));
        }
        setUndoToast(null);
      },
    });
  };

  // §3.5-2: Save event from modal
  const handleEventSave = async (eventId, updates) => {
    await calendarAPI.updateEvent(eventId, updates);
    // IDI-613: an all-day save drops the old times from the card too.
    const clear = updates.allDay ? { startTime: undefined, endTime: undefined, time: undefined } : {};
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, ...updates, ...clear } : e));
  };

  // §3.5-2: Delete event from modal
  const handleEventDelete = async (eventId) => {
    await calendarAPI.deleteEvent(eventId);
    setEvents(prev => prev.filter(e => e.id !== eventId));
  };

  // R8-R12: Resize an event's start/end time. Supports 30-min snap.
  // Optimistic update + undo toast + persistence.
  const handleEventResize = async (eventId, newEndHour, newStartHour) => {
    if (isFieldTech) return; // IDI-617 D3
    const event = events.find(e => e.id === eventId);
    if (!event) return;

    const originalStartTime = event.startTime || '';
    const originalEndTime = event.endTime || '';

    // Format fractional hours to HH:MM
    const formatHourToTime = (h) => {
      const hrs = Math.floor(Math.max(0, Math.min(23, h)));
      const mins = Math.round((h - hrs) * 60);
      return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    };
    const formatHourLabel = (h) => {
      const hrs = Math.floor(h);
      const mins = Math.round((h - hrs) * 60);
      return new Date(2000, 0, 1, hrs, mins).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };

    const newStartTime = newStartHour != null ? formatHourToTime(newStartHour) : originalStartTime;
    const newEndTime = newEndHour != null ? formatHourToTime(newEndHour) : originalEndTime;
    const startLabel = formatHourLabel(newStartHour != null ? newStartHour : eventStartHour(event));
    const endLabel = formatHourLabel(newEndHour != null ? newEndHour : (eventEndHour(event) || eventStartHour(event) + 1));

    // Optimistic update
    setEvents(prev => prev.map(e => {
      if (e.id !== eventId) return e;
      const updated = { ...e };
      if (newStartHour != null) updated.startTime = newStartTime;
      if (newEndHour != null) updated.endTime = newEndTime;
      return updated;
    }));

    setUndoToast({
      message: `Resized: ${event.title} \u00b7 ${startLabel} \u2013 ${endLabel}`,
      onUndo: () => {
        setEvents(prev => prev.map(e => (e.id === eventId ? { ...e, startTime: originalStartTime, endTime: originalEndTime } : e)));
        setUndoToast(null);
      },
      onDismiss: async () => {
        try {
          const updates = {};
          if (newStartHour != null) updates.startTime = newStartTime;
          if (newEndHour != null) updates.endTime = newEndTime;
          await calendarAPI.updateEvent(eventId, updates);
        } catch (err) {
          console.error('Failed to persist event resize:', err);
          setEvents(prev => prev.map(e => (e.id === eventId ? { ...e, startTime: originalStartTime, endTime: originalEndTime } : e)));
        }
        setUndoToast(null);
      },
    });
  };

  // §3.5-4: Assign an unscheduled job/WO to a calendar day (+optional time) via
  // drag-from-panel → drop-on-day. Optimistic + conflict check + persistence.
  const handleUnscheduledDrop = async (itemId, newDate, newTime) => {
    const item = unscheduledItems.find(i => (i.id || i.jobId || i.workOrderId) === itemId);
    if (!item) return;

    const startTime = newTime || '08:00';
    const newEvent = {
      id: item.id || itemId,
      title: item.title,
      type: item.type || 'work_order',
      date: newDate,
      startTime,
      time: `${newDate}T${startTime}:00`,
      customerName: item.customerName,
      techIds: item.techIds || [],
      crewId: item.crewId || item.crew_id,
      jobId: item.jobId,
      workOrderId: item.workOrderId,
    };

    // Optimistic: move from unscheduled panel onto the calendar
    setUnscheduledItems(prev => prev.filter(i => (i.id || i.jobId || i.workOrderId) !== itemId));
    setEvents(prev => [...prev, newEvent]);

    // §4.7 conflict check on drop
    const proposed = {
      startDate: newDate,
      endDate: newDate,
      techIds: newEvent.techIds,
      crewIds: newEvent.crewId ? [newEvent.crewId] : [],
    };
    const otherEvents = events.map(e => ({
      startDate: e.date || (e.time ? e.time.split('T')[0] : ''),
      endDate: e.date || (e.time ? e.time.split('T')[0] : ''),
      techIds: e.techIds || [],
      crewIds: e.crewId ? [e.crewId] : (e.crew_id ? [e.crew_id] : []),
    }));
    const conflicts = detectConflicts(proposed, { existing: otherEvents });
    setDragConflicts(conflicts);
    if (conflicts.length > 0) setTimeout(() => setDragConflicts([]), 5000);

    setUndoToast({
      message: `"${item.title}" scheduled for ${newDate}`,
      onUndo: () => {
        setEvents(prev => prev.filter(e => e.id !== newEvent.id));
        setUnscheduledItems(prev => [item, ...prev]);
        setUndoToast(null);
      },
      onDismiss: async () => {
        try {
          await calendarAPI.updateEvent(newEvent.id, { date: newDate, startTime });
        } catch (err) {
          console.error('Failed to persist scheduled item:', err);
          setEvents(prev => prev.filter(e => e.id !== newEvent.id));
          setUnscheduledItems(prev => [item, ...prev]);
        }
        setUndoToast(null);
      },
    });
  };

  // §4.9 ICS export/import (no OAuth)
  const handleExportIcs = () => downloadIcs(events, 'bidit-calendar.ics');
  const handleImportIcs = async (file) => {
    if (!file) return;
    const text = await file.text();
    const imported = parseIcs(text);
    setEvents((prev) => mergeImportedEvents(prev, imported));
  };

  // §4.1 filtered view + crew options
  const visibleEvents = events.filter((e) => {
    if (crewFilter && !eventCrewIds(e).includes(String(crewFilter))) return false;
    if (projectSearch) {
      const hay = `${e.title || ''} ${e.jobTitle || e.job_title || ''}`.toLowerCase();
      if (!hay.includes(projectSearch.toLowerCase())) return false;
    }
    return true;
  });
  const crewOptions = Array.from(
    new Map(
      events
        .filter((e) => e.crewId || e.crew_id)
        .map((e) => [e.crewId || e.crew_id, e.crewName || e.crew_name || (e.crewId || e.crew_id)])
    ).entries()
  );

  // Crew list for modal dropdown: Settings → Crews when loaded (IDI-476),
  // else the crews seen on events.
  const crews = settingsCrews.length
    ? settingsCrews.filter((c) => c && c.id != null).map((c) => ({ id: String(c.id), name: c.name || String(c.id), color: c.color }))
    : crewOptions.map(([id, name]) => ({ id, name }));

  const handleCreate = (type) => {
    if (type === 'appointment') setShowAppointmentSheet(true);
    else if (type === 'task') setShowTaskSheet(true);
    else if (type === 'bid') navigate('/quotes/add');
  };

  // eslint-disable-next-line no-unused-vars
  const getHeaderTitle = () => {
    if (view === 'month') {
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (view === 'week' || view === 'list') {
      const weekDays = getWeekDays(currentDate);
      const start = weekDays[0];
      const end = weekDays[6];
      if (start.getMonth() === end.getMonth()) {
        return `${MONTHS[start.getMonth()]} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()} – ${MONTHS[end.getMonth()].slice(0, 3)} ${end.getDate()}, ${end.getFullYear()}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <CalendarCrewsContext.Provider value={settingsCrews}>
    <div className="flex flex-col h-[calc(100vh-64px)] -mx-4 md:-mx-6 -mb-6">
      {/* Iter-18 CAL wires: title on its own row; controls on row 2. */}
      {/* Row 1: title (left) + More dropdown + Create (right).
          Iter-18: More replaces separate Export+Import buttons; menu offers
          Export as .ics (iCalendar/Google/Outlook — all three accept the same
          .ics payload) and Import. */}
      <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-12 py-3 flex-shrink-0">
        <h1 style={{ font: 'var(--text-h1)', color: 'var(--color-text-primary)', margin: 0 }}>Calendar</h1>
        <div className="flex items-center gap-2">
          <DropdownMenu
            ariaLabel="More actions"
            trigger={(
              <Button variant="secondary" aria-label="More actions">
                More
                <ChevronDownIcon className="h-4 w-4 ml-1" aria-hidden="true" />
              </Button>
            )}
            panelClassName="w-56"
            items={[
              {
                label: 'Export to iCalendar (.ics)',
                icon: <CalendarDaysIcon className="h-4 w-4" aria-hidden="true" />,
                onClick: handleExportIcs,
              },
              {
                label: 'Export to Google Calendar',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M8 5v3l2 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                ),
                title: 'Downloads a .ics file. Import into Google Calendar via Settings > Import & export.',
                onClick: handleExportIcs,
              },
              {
                label: 'Export to Outlook',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M4 6h8M4 8h8M4 10h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                ),
                title: 'Downloads a .ics file. Import into Outlook via File > Open & Export > Import/Export.',
                onClick: handleExportIcs,
              },
              { divider: true },
              {
                label: 'Import .ics file',
                icon: <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />,
                onClick: () => fileInputRef.current && fileInputRef.current.click(),
              },
            ]}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".ics,text/calendar"
            className="hidden"
            onChange={(e) => { handleImportIcs(e.target.files && e.target.files[0]); e.target.value = ''; }}
          />
          {/* IDI-617 D3: hidden (not disabled) for field techs. */}
          {!isFieldTech && <CreateDropdown onSelect={handleCreate} />}
        </div>
      </div>

      {/* Row 2: date nav (left) + view switcher / list toggle / unscheduled (right). */}
      <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-12 py-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="inline-flex items-center px-3 text-sm font-medium hover:bg-gray-50"
            style={{ height: 'var(--d-control-h)', border: '1px solid var(--color-border)', borderRadius: 'var(--btn-radius)', color: 'var(--color-text-default)' }}
          >
            Today
          </button>
          <button
            onClick={() => navigateDate(-1)}
            className="inline-flex items-center justify-center hover:bg-gray-50"
            style={{ height: 'var(--d-control-h)', width: 'var(--d-control-h)', border: '1px solid var(--color-border)', borderRadius: 'var(--btn-radius)' }}
            aria-label="Previous"
          >
            <ChevronLeftIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
          <button
            onClick={() => navigateDate(1)}
            className="inline-flex items-center justify-center hover:bg-gray-50"
            style={{ height: 'var(--d-control-h)', width: 'var(--d-control-h)', border: '1px solid var(--color-border)', borderRadius: 'var(--btn-radius)' }}
            aria-label="Next"
          >
            <ChevronRightIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
          {/* Iter-18: DateJumpPicker (pill-style trigger + mini-calendar popover)
              replaces the old <DatePicker> input. Same visual family as the
              Dashboard's PeriodSelector chip. */}
          <DateJumpPicker
            value={currentDate}
            onChange={(d) => setCurrentDate(d)}
            label="Jump to date"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* View Switcher — Iter-18 CAL wire: droplist of Month/Week/Day. */}
          <SelectMenu
            width="7rem"
            value={calView}
            onChange={(v) => setCalendarView(v, 'calendar')}
            aria-label="Calendar view"
            options={[
              { value: 'month', label: 'Month' },
              { value: 'week',  label: 'Week' },
              { value: 'day',   label: 'Day' },
            ]}
          />

          {/* Cal / List toggle — Iter-18 CAL wire: icon-only, separate 2-state segmented. */}
          <div className="inline-flex overflow-hidden" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--btn-radius)', height: 'var(--d-control-h)' }}>
            <button
              type="button"
              onClick={() => setCalendarView(calView, 'calendar')}
              className={`inline-flex items-center px-3 ${view !== 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 bg-white'}`}
              style={{ height: 'var(--d-control-h)' }}
              aria-label="Calendar view"
              aria-pressed={view !== 'list'}
              title="Calendar view"
            >
              <Squares2X2Icon className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setCalendarView(calView, 'list')}
              className={`inline-flex items-center px-3 border-l ${view === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 bg-white'}`}
              style={{ height: 'var(--d-control-h)', borderColor: 'var(--color-border)' }}
              aria-label="List view"
              aria-pressed={view === 'list'}
              title="List view"
            >
              <ListBulletIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Unscheduled toggle — no icon; numeric badge shows unscheduled + partially-scheduled Jobs count.
              IDI-617 D3: hidden for field techs (they can't schedule WOs). */}
          {!isFieldTech && (
          <Button
            variant={showUnscheduled ? 'primary' : 'secondary'}
            onClick={() => setShowUnscheduled(!showUnscheduled)}
            aria-label="Toggle Unscheduled panel"
          >
            Unscheduled
            {unscheduledItems.length > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-semibold"
                style={{ background: 'var(--color-action-primary)', color: 'var(--white)' }}
              >
                {unscheduledItems.length}
              </span>
            )}
          </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Calendar View */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* PR-42 §2c: ERROR state above the grid — distinct from EMPTY. */}
          {loadError && (
            <div className="px-6 pt-4">
              <ErrorState
                title="Could not load your calendar"
                message={loadError?.message || "We couldn't reach the calendar service. Try again in a moment."}
                size="sm"
                onRetry={() => fetchEvents()}
              />
            </div>
          )}
          {view === 'month' && (
            <MonthView
              currentDate={currentDate}
              events={visibleEvents}
              onDateClick={handleDateClick}
              onEventClick={handleEventClick}
              onEventDrop={handleEventDrop}
              onDayDoubleClick={handleDayDoubleClick}
            />
          )}
          {view === 'week' && (
            <WeekView
              currentDate={currentDate}
              events={visibleEvents}
              onEventClick={handleEventClick}
              onEventDrop={handleEventDrop}
              onUnscheduledDrop={handleUnscheduledDrop}
              onTimeSlotDoubleClick={handleTimeSlotDoubleClick}
              onDayHeaderDoubleClick={handleDayDoubleClick}
              onEventResize={handleEventResize}
              canResize={!isFieldTech}
            />
          )}
          {view === 'day' && (
            <DayView
              currentDate={currentDate}
              events={visibleEvents}
              onEventClick={handleEventClick}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
              onUnscheduledDrop={handleUnscheduledDrop}
              onTimeSlotDoubleClick={handleTimeSlotDoubleClick}
              canResize={!isFieldTech}
            />
          )}
          {view === 'list' && (
            <ListView
              currentDate={currentDate}
              events={visibleEvents}
              onEventClick={handleEventClick}
            />
          )}
        </div>

      </div>

      {/* Iter-18: Unscheduled is now a real right-side Slideover (was rendered
          as an in-flow side panel that stole horizontal space from the calendar). */}
      <Slideover
        open={showUnscheduled}
        onClose={() => setShowUnscheduled(false)}
        title="Unscheduled"
        size="sm"
      >
        <div className="p-3 space-y-2">
          <p className="text-xs text-gray-500">{unscheduledItems.length} items awaiting scheduling</p>
          {unscheduledItems.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">All items are scheduled</p>
          ) : (
            unscheduledItems.map((item, idx) => {
              const itemId = item.id || item.jobId || item.workOrderId;
              const handleDragStart = (e) => {
                e.dataTransfer.setData('text/plain', JSON.stringify({ id: itemId, type: 'unscheduled-item' }));
                e.dataTransfer.effectAllowed = 'move';
              };
              return (
                <div
                  key={itemId || idx}
                  draggable
                  onDragStart={handleDragStart}
                  className="card px-3 py-2 cursor-grab hover:border-[color:var(--color-border)] transition"
                  title="Drag onto a calendar day to schedule"
                >
                  <p className="text-xs font-medium text-gray-900 truncate">{item.title}</p>
                  <p className="text-[10px] text-gray-500">{item.customerName || item.type}</p>
                </div>
              );
            })
          )}
        </div>
      </Slideover>

      {/* Side-Sheets */}
      {showCreateSheet && (
        <EventCreateSheet
          open={showCreateSheet}
          onClose={() => setShowCreateSheet(false)}
          initialType={createSheetType}
          defaults={createSheetDefaults}
          events={events}
          onSubmit={async (data) => {
            const isTask = data.type === 'task';
            const optimistic = { ...data, id: `${data.type}-${Date.now()}` };
            setEvents(prev => [...prev, optimistic]);
            try {
              const saved = isTask
                ? await calendarAPI.createTask(data)
                : await calendarAPI.createAppointment(data);
              if (saved && saved.id) {
                // Merge: keep optimistic fields as fallback, overlay API response
                setEvents(prev => prev.map(e => {
                  if (e.id !== optimistic.id) return e;
                  return {
                    ...optimistic,
                    ...saved,
                    date: saved.date || saved.dueDate || saved.scheduled_date || optimistic.date,
                    startTime: saved.startTime || saved.start_time || optimistic.startTime,
                    endTime: saved.endTime || saved.end_time || optimistic.endTime,
                    type: saved.type || optimistic.type,
                  };
                }));
              }
            } catch (err) {
              setEvents(prev => prev.filter(e => e.id !== optimistic.id));
            }
          }}
          defaultDate={currentDate}
        />
      )}

      {/* Conflict Warnings (§4.7) */}
      {dragConflicts.length > 0 && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-40 w-96">
          <ConflictWarnings warnings={dragConflicts} />
        </div>
      )}

      {/* Undo Toast (§3.5-5) */}
      {undoToast && (
        <UndoToast
          message={undoToast.message}
          onUndo={undoToast.onUndo}
          onDismiss={undoToast.onDismiss}
          duration={3000}
        />
      )}

      {/* Event Quick-View Modal (§3.5-2) */}
      {selectedEvent && (
        <CalendarEventModal
          event={selectedEvent}
          crews={crews}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
          onClose={() => setSelectedEvent(null)}
          readOnly={isFieldTech}
        />
      )}
    </div>
    </CalendarCrewsContext.Provider>
  );
}


// ─── Unified Event Create Sheet (R6/R7) ──────────────────────────────────────

function EventCreateSheet({ open, onClose, onSubmit, initialType = 'appointment', defaults = {}, defaultDate, events: allEvents = [] }) {
  const resolvedDate = defaults.date || (defaultDate ? ymdOfDate(defaultDate) : '');
  const [eventType, setEventType] = useState(initialType);
  const [form, setForm] = useState({
    title: '',
    date: resolvedDate,
    endDate: resolvedDate,
    startTime: defaults.startTime || '09:00',
    endTime: defaults.endTime || '10:00',
    location: '',
    customerId: '',
    customerName: '',
    assignees: [],
    crewId: '',
    notes: '',
    recurrence: 'none',
    recurrenceEndDate: '',
    allDay: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Load customers + users + crews for typeaheads/dropdowns.
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [crews, setCrews] = useState([]);
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    Promise.all([
      customerAPI.getCustomers().catch(() => []),
      settingsAPI.getUsers().catch(() => []),
      settingsAPI.getList('crews').catch(() => []),
      // IDI-52 (B3): migrated field-technician roster is the primary assignment
      // source; Cognito users remain as a fallback for net-new tenants. This is
      // the create-appointment flow's own users state (separate from
      // CalendarEventModal's) — it needs the same fix.
      settingsAPI.getFieldTechnicians().catch(() => []),
    ]).then(([c, u, cr, techs]) => {
      if (ignore) return;
      // IDI-100: use the shared unwrapper instead of the ad hoc
      // Array.isArray(c) ? c : (Array.isArray(c?.data) ? c.data : []) check,
      // which read the wrong key (`data`, not `customers`) for the enveloped
      // shape — harmless today only because this call passes no `limit` and
      // therefore never receives that shape, but it would have silently
      // emptied the dropdown the moment this call gained pagination.
      setCustomers(unwrapList(c, 'customers', 'CalendarPage.createAppointment').list);
      const cognitoUsers = Array.isArray(u) ? u : (Array.isArray(u?.data) ? u.data : (Array.isArray(u?.users) ? u.users : []));
      const fieldTechs = Array.isArray(techs) ? techs : (Array.isArray(techs?.data) ? techs.data : []);
      // IDI-704 (IDI-681 D1): a deactivated login is not offered, neither
      // from list_users nor brought back by a roster row with the same id.
      const deactivatedIds = deactivatedUserIds(cognitoUsers);
      const byId = new Map();
      fieldTechs.forEach(t => { const id = t.id || t.name; if (id && !deactivatedIds.has(String(id))) byId.set(String(id), { id, name: t.name || t.email || id }); });
      cognitoUsers.forEach(cu => { if (!cu || isDeactivatedStatus(cu.status)) return; const id = cu.id || cu.name; if (id && !byId.has(String(id))) byId.set(String(id), { id, name: cu.name || cu.email || id }); });
      setUsers(Array.from(byId.values()));
      setCrews(Array.isArray(cr) ? cr : (Array.isArray(cr?.data) ? cr.data : []));
    });
    return () => { ignore = true; };
  }, [open]);

  // Reset form when sheet opens with new defaults
  useEffect(() => {
    if (open) {
      setEventType(initialType);
      const dateVal = defaults.date || (defaultDate ? ymdOfDate(defaultDate) : '');
      setForm({
        title: '',
        date: dateVal,
        endDate: dateVal,
        startTime: defaults.startTime || '09:00',
        endTime: defaults.endTime || '10:00',
        location: '',
        customerId: '',
        customerName: '',
        assignees: [],
        crewId: '',
        notes: '',
        recurrence: 'none',
        recurrenceEndDate: '',
        allDay: false, // IDI-613 (D1): unchecked on open
      });
      setCustomerSearch('');
      setShowCustomerDropdown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return false;
    const q = customerSearch.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.company_name || '').toLowerCase().includes(q);
  }).slice(0, 8);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const { allDay, startTime, endTime, ...rest } = form;
      await onSubmit({
        ...rest,
        // IDI-609 (D1): endDate only for a span (after the start date).
        endDate: rest.endDate && rest.endDate > rest.date ? rest.endDate : undefined,
        // IDI-613 (D1): All day saves allDay:true and no times.
        ...(allDay ? { allDay: true } : { startTime, endTime, time: `${form.date}T${startTime}:00` }),
        // IDI-476: the picked Settings crew; omitted when none.
        crewId: form.crewId ? String(form.crewId) : undefined,
        type: eventType,
      });
      onClose();
    } catch (err) {
      console.error('Failed to create event:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-[480px] bg-white flex flex-col">
        {/* Header with type toggle (R6) */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-1 bg-gray-100 p-0.5" style={{ borderRadius: 'var(--radius-full)' }}>
            <button
              onClick={() => setEventType('appointment')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                eventType === 'appointment'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarDaysIcon className="h-4 w-4" />
              Appointment
            </button>
            <button
              onClick={() => setEventType('task')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                eventType === 'task'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CheckIcon className="h-4 w-4" />
              Task
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form Fields */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <Input
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder={eventType === 'task' ? 'Task title' : 'Appointment title'}
            />
          </div>

          {/* Dates (R13-R15: Two-month range selector) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Dates <span className="text-red-500">*</span></label>
            <DateRangeSelector
              value={form.date ? { start: form.date, end: form.endDate || form.date } : null}
              onChange={({ start, end }) => {
                handleChange('date', start);
                handleChange('endDate', end);
              }}
              placeholder="Select date range"
            />
          </div>

          {/* IDI-613 (D1): All day hides the times */}
          <Checkbox
            checked={!!form.allDay}
            onChange={(checked) => handleChange('allDay', checked)}
            label="All day"
          />

          {/* Time (shown for both types per wire — identical field set) */}
          {!form.allDay && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <TimePicker
                label="Start Time"
                value={form.startTime}
                onChange={(val) => handleChange('startTime', val)}
              />
            </div>
            <div>
              <TimePicker
                label="End Time"
                value={form.endTime}
                onChange={(val) => handleChange('endTime', val)}
              />
            </div>
          </div>
          )}

          {/* Recurrence (positioned under time pickers per operator feedback) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Recurrence</label>
            <SelectMenu
              value={form.recurrence}
              onChange={(v) => handleChange('recurrence', v)}
              options={[
                { value: 'none', label: 'None' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'biweekly', label: 'Bi-weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
            />
          </div>
          {form.recurrence !== 'none' && (
            <DatePicker
              label="Repeat Until"
              value={form.recurrenceEndDate}
              onChange={(v) => handleChange('recurrenceEndDate', v)}
            />
          )}

          {/* Customer */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Customer</label>
            {form.customerId ? (
              <div className="input flex items-center gap-2">
                <Chip>{form.customerName || 'Unnamed'}</Chip>
                <button onClick={() => { handleChange('customerId', ''); handleChange('customerName', ''); }} className="text-gray-400 hover:text-gray-600 ml-auto">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                  onFocus={() => { if (customerSearch) setShowCustomerDropdown(true); }}
                  placeholder="Search customers..."
                />
                {showCustomerDropdown && filteredCustomers.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 w-full bg-white border max-h-48 overflow-y-auto" style={{ borderColor: 'var(--color-border)', borderRadius: 'var(--r-base)' }}>
                    {filteredCustomers.map(c => (
                      <button
                        key={c.id}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                        onClick={() => {
                          handleChange('customerId', c.id);
                          handleChange('customerName', c.name || c.company_name || 'Unnamed');
                          setCustomerSearch('');
                          setShowCustomerDropdown(false);
                        }}
                      >
                        <div className="font-medium">{c.name || c.company_name}</div>
                        {c.company_name && c.name && <div className="text-xs text-gray-500">{c.company_name}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Location combobox */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
            <LocationCombobox
              value={form.location}
              onChange={(val) => handleChange('location', val)}
              events={allEvents}
            />
          </div>

          {/* Assignees */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Assignees</label>
            <TypeaheadChips
              value={form.assignees}
              onChange={(arr) => handleChange('assignees', arr)}
              options={users.map(u => ({ value: u.id || u.name, label: u.name || u.email }))}
              placeholder="Add team members"
              aria-label="Assignees"
            />
          </div>

          {/* Crew */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Crew</label>
            {/* IDI-476: a typeahead over Settings → Crews (single pick, colour
                swatch), like the WO create sheet. Its id is saved on the
                event (calendar Lambda `crewId`) so the card takes its colour. */}
            <TypeaheadChips
              variant="identity"
              options={crewPickerOptions(crews)}
              value={form.crewId ? [String(form.crewId)] : []}
              onChange={(next) => {
                const arr = Array.isArray(next) ? next : [];
                handleChange('crewId', arr[arr.length - 1] || '');
              }}
              placeholder="Search crews (optional)"
              aria-label="Crew"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add notes..."
              className="input w-full min-h-[80px] resize-y"
            />
          </div>

        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={!form.title.trim()}>
            {eventType === 'task' ? 'Create Task' : 'Create Appointment'}
          </Button>
        </div>
      </div>
    </>
  );
}
